import { useState } from 'react';
import type { ITripLegDetails } from '../../../../../../../../models/trip-leg-details.model';
import type { IServiceJourneyDetails } from '../../../../../../../../models/service-journey-details.model';
import type { ICallDetails } from '../../../../../../../../models/call-details.model';
import './style.scss';
import type { ITripLeg } from '../../../../../../../../models/trip-leg.model';
import { DirectionHelper } from '../../../../../../../../helpers/direction.helper';
import WheelchairIcon from '../../../../../../../Shared/WheelchairIcon';
import { TranslationService } from '../../../../../../../../services/translation.service';
import Call from './components/Call';
import { AnalyticsService } from '../../../../../../../../services/analytics-service';
import { WheelchairIconType } from '../../../../../../../Shared/WheelchairIcon/wheelchair-icon-type.enum';
import LineBadge from '../../../../../../../Shared/LineBadge';
import Notes from '../TripLeg/components/Notes';
import { LegHelper } from '../../../../../../../../helpers/leg.helper';

function TripLegCalls({ tripLeg, tripLegDetails, journeyId, onClose }: { tripLeg: ITripLeg; tripLegDetails: ITripLegDetails | undefined; journeyId: number; onClose: () => void }) {
  const [showCallsBeforeTripLeg, setShowCallsBeforeTripLeg] = useState<boolean>(false);
  const [showCallsAfterTripLeg, setShowCallsAfterTripLeg] = useState<boolean>(false);

  const hasAnyCallOnTripLeg = (x: IServiceJourneyDetails) => x.callsOnServiceJourney?.some((c) => c.isOnTripLeg) ?? false;
  const serviceJourneysWithCalls = tripLegDetails?.serviceJourneys?.filter(hasAnyCallOnTripLeg);
  const allNotes = LegHelper.getAllNotes(tripLeg);

  const callsBeforeTripLeg: ICallDetails[] = [];
  const callsOnTripLeg: ICallDetails[] = [];
  const callsAfterTripLeg: ICallDetails[] = [];

  serviceJourneysWithCalls?.forEach((serviceJourney) => {
    serviceJourney.callsOnServiceJourney?.forEach((call) => {
      // NV-7151: For journeys with multiple service journeys, do not add calls twice
      if (callsOnTripLeg.length > 0 && callsOnTripLeg[callsOnTripLeg.length - 1].stopPoint.gid === call.stopPoint.gid) {
        return;
      }
      if (call.isOnTripLeg) {
        callsOnTripLeg.push(call);
      } else if (callsOnTripLeg.length === 0) {
        callsBeforeTripLeg.push(call);
      } else {
        callsAfterTripLeg.push(call);
      }
    });
  });

  return (
    <>
      <div className="trip-leg-calls position-relative">
        <div className="d-flex justify-content-between border-bottom mb-2 pb-1">
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0">{tripLeg.serviceJourney.line && <LineBadge line={tripLeg.serviceJourney.line} className="me-1" />}</div>
            <span className="align-self-center flex-grow-1 text-color-gray-900">{DirectionHelper.getDirectionForDisplay(tripLeg.serviceJourney)}</span>
            {tripLeg.serviceJourney.line && tripLeg.serviceJourney.line.isWheelchairAccessible && <WheelchairIcon className="ms-1" iconType={WheelchairIconType.dark} sizeInRem={1.5} />}
          </div>
          <button type="button" className="d-none d-md-block btn-close trip-leg-calls__btn-close d-print-none" aria-label={TranslationService.get('common.close')} onClick={onClose}></button>
          <button
            type="button"
            className="d-md-none btn-close trip-leg-calls__btn-close d-print-none"
            aria-label={TranslationService.get('common.close')}
            onClick={onClose}
            aria-controls={`journey-details-map-${journeyId}`}
            aria-expanded="true"
          ></button>
        </div>
        {callsBeforeTripLeg.length > 0 && !showCallsBeforeTripLeg && (
          <button
            type="button"
            className="btn btn-tertiary btn-icon-right btn-icon-caret-up mb-2 d-print-none"
            onClick={() => {
              setShowCallsBeforeTripLeg(!showCallsBeforeTripLeg);
              AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa tidigare hållplatser', '');
            }}
          >
            {TranslationService.get('travelplanner.showearlierstops')}
          </button>
        )}
        <ol className="trip-leg-calls list-unstyled">
          <li hidden={!showCallsBeforeTripLeg}>
            <ol className="list-unstyled">
              {callsBeforeTripLeg.map((call, index) => (
                <Call
                  key={`call-before-trip-${call.stopPoint.gid}`}
                  tripLeg={tripLeg}
                  callsAfterTripLeg={callsAfterTripLeg}
                  callsBeforeTripLeg={callsBeforeTripLeg}
                  callsOnTripLeg={callsOnTripLeg}
                  call={call}
                  callIndex={index}
                  isDestination={false}
                  isOnTripLeg={false}
                  extendsFromCall={false}
                  extendsToCall={false}
                  showCallsAfterTripLeg={showCallsAfterTripLeg}
                  showCallsBeforeTripLeg={showCallsBeforeTripLeg}
                />
              ))}
            </ol>
          </li>
          <li>
            <ol className="list-unstyled">
              {callsOnTripLeg.map((call, index) => (
                <Call
                  key={`call-on-trip-${call.stopPoint.gid}`}
                  tripLeg={tripLeg}
                  callsAfterTripLeg={callsAfterTripLeg}
                  callsBeforeTripLeg={callsBeforeTripLeg}
                  callsOnTripLeg={callsOnTripLeg}
                  call={call}
                  callIndex={index + callsBeforeTripLeg.length}
                  isDestination={index === callsOnTripLeg.length - 1}
                  isOnTripLeg={true}
                  extendsFromCall={index === 0 && callsBeforeTripLeg.length > 0}
                  extendsToCall={index === callsOnTripLeg.length - 1 && callsAfterTripLeg.length > 0}
                  showCallsAfterTripLeg={showCallsAfterTripLeg}
                  showCallsBeforeTripLeg={showCallsBeforeTripLeg}
                />
              ))}
            </ol>
          </li>
          <li hidden={!showCallsAfterTripLeg}>
            <ol className="list-unstyled">
              {callsAfterTripLeg.map((call, index) => (
                <Call
                  key={`call-after-trip-${call.stopPoint.gid}`}
                  tripLeg={tripLeg}
                  callsAfterTripLeg={callsAfterTripLeg}
                  callsBeforeTripLeg={callsBeforeTripLeg}
                  callsOnTripLeg={callsOnTripLeg}
                  call={call}
                  callIndex={index + callsBeforeTripLeg.length + callsOnTripLeg.length}
                  isDestination={false}
                  isOnTripLeg={false}
                  extendsFromCall={false}
                  extendsToCall={false}
                  showCallsAfterTripLeg={showCallsAfterTripLeg}
                  showCallsBeforeTripLeg={showCallsBeforeTripLeg}
                />
              ))}
            </ol>
          </li>
        </ol>
        {callsAfterTripLeg.length > 0 && !showCallsAfterTripLeg && (
          <button
            type="button"
            className="btn btn-tertiary btn-icon-right btn-icon-caret-down d-print-none collapsed"
            onClick={() => {
              setShowCallsAfterTripLeg(!showCallsAfterTripLeg);
              AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa senare hållplatser', '');
            }}
          >
            {TranslationService.get('travelplanner.showlaterstops')}
          </button>
        )}
      </div>
      {allNotes && allNotes.length > 0 && <Notes notes={allNotes} className="mt-4" />}
    </>
  );
}

export default TripLegCalls;

